import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Lock, FileCheck, History, Shield, Server } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const securityFeatures = [
  {
    icon: Lock,
    title: 'Encryption at rest and in transit',
    description: 'AES-256 encryption for all data',
  },
  {
    icon: FileCheck,
    title: 'PDPA (Singapore) baseline + GDPR-ready',
    description: 'Full compliance framework',
  },
  {
    icon: History,
    title: 'Configurable data retention + audit logs',
    description: 'Complete activity tracking',
  },
  {
    icon: Shield,
    title: 'Least-privilege access + 2FA',
    description: 'Enterprise-grade security',
  },
];

export default function SecuritySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const leftBlockRef = useRef<HTMLDivElement>(null);
  const rightCardRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Left block
      gsap.fromTo(
        leftBlockRef.current,
        { x: '-4vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: leftBlockRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Right card
      gsap.fromTo(
        rightCardRef.current,
        { x: '4vw', opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.6,
          delay: 0.1,
          scrollTrigger: {
            trigger: rightCardRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Features stagger
      featuresRef.current.forEach((feature, index) => {
        if (feature) {
          gsap.fromTo(
            feature,
            { y: 20, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.4,
              delay: 0.2 + index * 0.1,
              scrollTrigger: {
                trigger: leftBlockRef.current,
                start: 'top 70%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy relative z-[110]"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left - Features List */}
          <div ref={leftBlockRef} className="opacity-0">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
              Security
            </span>
            <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-8">
              Security and compliance, by design
            </h2>

            <div className="space-y-6">
              {securityFeatures.map((feature, index) => (
                <div
                  key={feature.title}
                  ref={(el) => { featuresRef.current[index] = el; }}
                  className="flex items-start gap-4 opacity-0"
                >
                  <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-slate">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Compliance Card */}
          <div ref={rightCardRef} className="opacity-0">
            <div className="surface-panel p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                  <Server className="w-6 h-6 text-teal" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    Data handling
                  </h3>
                </div>
              </div>

              <p className="text-slate mb-6 leading-relaxed">
                Consent-first collection, clear processing purposes, and easy
                export or deletion requests. Your data stays in APAC regions by
                default.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="font-heading text-2xl font-bold text-teal mb-1">
                    SOC 2
                  </p>
                  <p className="text-xs text-slate">Type II Certified</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="font-heading text-2xl font-bold text-teal mb-1">
                    PDPA
                  </p>
                  <p className="text-xs text-slate">Compliant</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="font-heading text-2xl font-bold text-teal mb-1">
                    GDPR
                  </p>
                  <p className="text-xs text-slate">Ready</p>
                </div>
                <div className="p-4 bg-white/5 rounded-lg text-center">
                  <p className="font-heading text-2xl font-bold text-teal mb-1">
                    99.9%
                  </p>
                  <p className="text-xs text-slate">Uptime SLA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
